import { Button, InputText, Checkbox } from "primereact";
import { TabView, TabPanel } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  addContract,
  getContracts,
  getCustomersURL,
  getLevies,
  getUnitsForFacility,
  getTaxRates
} from "../../../../utils/urls";
import Layout from "../../component/layout";

const AddContract = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // State management
  const [contractName, setContractName] = useState("");
  const [levy, setLevy] = useState("");
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [amount, setAmount] = useState("");
  const [balanceBroughtForward, setBalanceBroughtForward] = useState("0.00");
  const [enabledTaxes, setEnabledTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMultipleMode, setIsMultipleMode] = useState(false);
  const [fetchedTaxRates, setFetchedTaxRates] = useState([]);

  // STATUS / ACTIVATION STATE (replaces start/end date inputs)
  const [isActive, setIsActive] = useState(false); // Default: Inactive
  const [startDate, setStartDate] = useState("");

  // Review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  // Official receipts returned by the server after creation (shown with a download button)
  const [issuedReceipts, setIssuedReceipts] = useState([]);
  const [showIssuedReceipts, setShowIssuedReceipts] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);
  const [showLevyDetails, setShowLevyDetails] = useState(false);

  // UPFRONT PAYMENT STATE (simplified - months + total only)
  const [upfrontPaymentEnabled, setUpfrontPaymentEnabled] = useState(false);
  const [upfrontAmount, setUpfrontAmount] = useState("");
  const [upfrontAmountPerMonth, setUpfrontAmountPerMonth] = useState("");
  const [numberOfMonths, setNumberOfMonths] = useState("");
  const [customTotalAmount, setCustomTotalAmount] = useState("");
  const [upfrontCalculationMode, setUpfrontCalculationMode] = useState('auto');
  // 'invoices' = generate paid invoices now; 'credit' = hold as overpayment for scheduled invoices
  const [upfrontSettlement, setUpfrontSettlement] = useState('invoices');

  // DEPOSIT STATE — CONFIG ONLY. This defines how much the deposit IS
  // (just like upfrontPayment defines how much the upfront charge is).
  // It no longer carries datePaid/paymentReference — that data now lives
  // on the Payment tab below, which captures what was ACTUALLY paid.
  const [depositEnabled, setDepositEnabled] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositCalculationMode, setDepositCalculationMode] = useState('auto');
  const [depositNumberOfMonths, setDepositNumberOfMonths] = useState("");
  const [depositCustomTotalAmount, setDepositCustomTotalAmount] = useState("");
  const [depositAmountPerMonth, setDepositAmountPerMonth] = useState(""); // Loaded from levy

  // PAYMENT STATE — NEW. This is the single source of truth for the actual
  // money received against this contract (deposit + upfront charges
  // combined). Independent of how those charges are configured, so it
  // naturally supports over/under payment.
  const [paymentAmountPaid, setPaymentAmountPaid] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("mpesa");

  // Data lists
  const [levies, setLevies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [existingContracts, setExistingContracts] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [selectedLevy, setSelectedLevy] = useState(null);
  const [unitCustomerMap, setUnitCustomerMap] = useState({});

  // ============================================
  // AUTO-CALCULATE TOTALS
  // ============================================

  // Auto-calculate total upfront amount
  useEffect(() => {
    if (upfrontCalculationMode === 'auto' && upfrontAmountPerMonth && numberOfMonths) {
      const total = parseFloat(upfrontAmountPerMonth) * parseInt(numberOfMonths);
      if (!isNaN(total) && total > 0) {
        setUpfrontAmount(total.toFixed(2));
      }
    }
  }, [upfrontAmountPerMonth, numberOfMonths, upfrontCalculationMode]);

  // When custom total is entered, update upfront amount
  useEffect(() => {
    if (upfrontCalculationMode === 'custom' && customTotalAmount) {
      setUpfrontAmount(customTotalAmount);
    }
  }, [customTotalAmount, upfrontCalculationMode]);

  // Reset fields when switching modes
  // FIX: previously this also cleared `upfrontAmountPerMonth`, which is a
  // read-only value loaded from the levy. Wiping it meant that switching
  // back to "Auto Calculate" had nothing to multiply against, so the total
  // never recalculated. We now ONLY clear the fields that are specific to
  // each mode (numberOfMonths for auto, customTotalAmount for custom).
  useEffect(() => {
    if (upfrontCalculationMode === 'auto') {
      setCustomTotalAmount("");
    } else {
      setNumberOfMonths("");
    }
  }, [upfrontCalculationMode]);

  // Auto-calculate total deposit amount
  useEffect(() => {
    if (depositCalculationMode === 'auto' && depositAmountPerMonth && depositNumberOfMonths) {
      const total = parseFloat(depositAmountPerMonth) * parseInt(depositNumberOfMonths);
      if (!isNaN(total) && total > 0) {
        setDepositAmount(total.toFixed(2));
      }
    }
  }, [depositAmountPerMonth, depositNumberOfMonths, depositCalculationMode]);

  // When custom total is entered, update deposit amount
  useEffect(() => {
    if (depositCalculationMode === 'custom' && depositCustomTotalAmount) {
      setDepositAmount(depositCustomTotalAmount);
    }
  }, [depositCustomTotalAmount, depositCalculationMode]);

  // Reset fields when switching modes
  // FIX: same issue as above - never clear `depositAmountPerMonth`, it's
  // read-only / levy-derived and must persist across mode switches.
  useEffect(() => {
    if (depositCalculationMode === 'auto') {
      setDepositCustomTotalAmount("");
    } else {
      setDepositNumberOfMonths("");
    }
  }, [depositCalculationMode]);

  // Reset start date if contract is set back to inactive
  useEffect(() => {
    if (!isActive) {
      setStartDate("");
    }
  }, [isActive]);

  const fetchTaxRates = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getTaxRates}/${facilityId}`,
        "GET"
      );
      if (response.success && response.data) {
        const taxData = response.data.data || response.data;
        const taxRatesArray = Array.isArray(taxData) ? taxData : [taxData];
        setFetchedTaxRates(taxRatesArray);
      }
    } catch (error) {
      console.error("Error fetching tax rates:", error);
      toastify("Error fetching tax rates", "error");
    }
  };

  // Fetch existing contracts
  const fetchExistingContracts = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getContracts}/${facilityId}`, "GET");
      if (response.success) {
        const contractsData = response.data?.contracts || response.data || [];
        setExistingContracts(contractsData);
      }
    } catch (error) {
      console.error("Error fetching existing contracts:", error);
      setExistingContracts([]);
    }
  };

  // Fetch available levies
  const fetchLevies = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getLevies}/${facilityId}`,
        "GET",
        {}
      );

      if (response.success) {
        let leviesData = [];

        if (Array.isArray(response.data)) {
          leviesData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          leviesData = response.data.data;
        } else if (typeof response.data === 'object' && response.data !== null) {
          leviesData = [response.data];
        }

        const processedLevies = leviesData.map((levy) => ({
          _id: levy._id,
          levyName: levy.levyName || 'Untitled Levy',
          amount: levy.amount || 0,
          levyType: levy.levyType?.name || "N/A",
          collectionFrequency: levy.collectionFrequency || "Monthly",
          levyApplicant: levy.levyApplicant || "home owner"
        }));

        setLevies(processedLevies);
      } else {
        throw new Error(response.error || "Failed to fetch levies");
      }
    } catch (err) {
      console.error("Error in fetchLevies:", err);
      toastify(err.message || "Error fetching levies", "error");
      setLevies([]);
    }
  };

  // ============================================
  // TAX BREAKDOWN - applies ONLY to upfront payment, never to deposit
  // ============================================
  const upfrontTaxBreakdown = (() => {
    if (!upfrontPaymentEnabled || !upfrontAmount || enabledTaxes.length === 0) return [];
    const base = parseFloat(upfrontAmount) || 0;
    return fetchedTaxRates
      .filter(t => enabledTaxes.includes(t._id))
      .map(t => ({
        label: `${t.taxType.toUpperCase()} (${t.taxRate}%)`,
        amount: ((base * t.taxRate) / 100).toFixed(2),
        rate: t.taxRate,
      }));
  })();

  const upfrontTaxTotal = upfrontTaxBreakdown.reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const upfrontTotalWithTax = (() => {
    const base = parseFloat(upfrontAmount) || 0;
    return (base + upfrontTaxTotal).toFixed(2);
  })();

  // Total charges = upfront (incl. tax) + deposit (never taxed).
  // This is what's OWED — separate from what's actually paid (Payment tab).
  const totalCharges = (() => {
    const upfrontTotal = upfrontPaymentEnabled ? parseFloat(upfrontTotalWithTax) || 0 : 0;
    const depositTotal = depositEnabled ? parseFloat(depositAmount) || 0 : 0;
    return (upfrontTotal + depositTotal).toFixed(2);
  })();

  // Balance due / overpayment, computed from the explicit payment amount
  // entered on the Payment tab — never derived from summing the charge legs.
  const balanceDue = (() => {
    const paid = parseFloat(paymentAmountPaid) || 0;
    return (parseFloat(totalCharges) - paid).toFixed(2);
  })();
  const isOverpaid = parseFloat(balanceDue) < 0;
  const overpaymentAmount = isOverpaid ? Math.abs(parseFloat(balanceDue)).toFixed(2) : "0.00";

  // Fetch available customers for the facility
  const fetchCustomersForFacility = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getCustomersURL}/${facilityId}`,
        "GET",
        {}
      );
      if (response.success) {
        const normalizedCustomers = response.data.map(customer => ({
          ...customer,
          customerType: customer.customerType?.trim().toLowerCase() || ""
        }));

        setCustomers(normalizedCustomers);
      } else {
        throw new Error("Failed to fetch customers.");
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Fetch available units for the facility
  const fetchUnitsForFacility = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getUnitsForFacility}/${facilityId}`,
        "GET",
        {}
      );
      if (response.success) {
        const filteredUnits = response.data.filter(unit =>
          unit.tenantId || unit.homeOwnerId
        );

        setUnits(filteredUnits);

        // Initially set available units to all valid units
        updateAvailableUnits(filteredUnits, existingContracts, levy);
      } else {
        throw new Error("Failed to fetch units.");
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Update available units based on levy selection and existing contracts
  const updateAvailableUnits = (unitsList, contractsList, selectedLevyId) => {
    if (!selectedLevyId) {
      setAvailableUnits(unitsList);
      return;
    }

    // Filter out units that already have an active contract for the selected levy
    const filteredUnits = unitsList.filter(unit => {
      const hasExistingContract = contractsList.some(contract =>
        contract.unitId === unit._id &&
        contract.levyId === selectedLevyId &&
        contract.status === 'Active'
      );

      return !hasExistingContract;
    });

    setAvailableUnits(filteredUnits);

    // Update unit-customer mapping
    const mapping = {};
    filteredUnits.forEach(unit => {
      const customer = determineCustomerForUnit(unit);
      if (customer) {
        mapping[unit._id] = customer;
      }
    });
    setUnitCustomerMap(mapping);
  };

  const determineCustomerForUnit = (unit) => {
    if (!selectedLevy) return null;

    const levyApplicantLower = selectedLevy.levyApplicant.toLowerCase();
    let customerId = null;
    let customerInfo = null;

    if (levyApplicantLower === "tenant" || levyApplicantLower === "tenants") {
      customerId = unit.tenantId || unit.homeOwnerId;
    } else {
      customerId = unit.homeOwnerId || unit.tenantId;
    }

    if (customerId) {
      const customer = customers.find(c => c._id === customerId);
      if (customer) {
        customerInfo = {
          id: customer._id,
          name: `${customer.firstName} ${customer.lastName}`,
          type: customer.customerType,
          isCorrectType: (levyApplicantLower === "tenant" && unit.tenantId === customerId) ||
            (levyApplicantLower !== "tenant" && unit.homeOwnerId === customerId)
        };
      }
    }

    return customerInfo;
  };

  const handleUnitSelect = (unitId) => {
    if (isMultipleMode) {
      // Multiple mode - toggle selection
      setSelectedUnits(prev => {
        if (prev.includes(unitId)) {
          return prev.filter(id => id !== unitId);
        } else {
          return [...prev, unitId];
        }
      });
    } else {
      // Single mode - replace selection
      setSelectedUnits([unitId]);
    }
  };

  const handleSelectAllUnits = () => {
    if (selectedUnits.length === availableUnits.length) {
      setSelectedUnits([]);
    } else {
      setSelectedUnits(availableUnits.map(unit => unit._id));
    }
  };

  // Validate the form. Returns true if valid, shows toast + returns false if not.
  const validateForm = () => {
    if (!contractName || contractName.trim() === "") {
      toastify("Levy Agreement name is required.", "error");
      return false;
    }
    if (!levy) {
      toastify("Levy is required.", "error");
      return false;
    }
    if (selectedUnits.length === 0) {
      toastify("At least one unit must be selected.", "error");
      return false;
    }
    if (!amount || isNaN(amount)) {
      toastify("Amount must be a valid number.", "error");
      return false;
    }
    if (isActive && !startDate) {
      toastify("Start date is required when contract is set to Active.", "error");
      return false;
    }

    // Upfront payment validation (config only)
    if (upfrontPaymentEnabled) {
      if (!upfrontAmount || isNaN(upfrontAmount) || parseFloat(upfrontAmount) <= 0) {
        toastify("Valid pre-payment amount is required.", "error");
        return false;
      }
      if (upfrontCalculationMode === 'auto' && !numberOfMonths) {
        toastify("Number of months is required for auto calculation.", "error");
        return false;
      }
      if (upfrontCalculationMode === 'custom' && !customTotalAmount) {
        toastify("Custom total amount is required.", "error");
        return false;
      }
    }

    // Deposit validation (config only — no date/reference here anymore)
    if (depositEnabled) {
      if (!depositAmount || isNaN(depositAmount) || parseFloat(depositAmount) <= 0) {
        toastify("Valid deposit amount is required.", "error");
        return false;
      }
      if (depositCalculationMode === 'auto' && !depositNumberOfMonths) {
        toastify("Number of months is required for auto calculation.", "error");
        return false;
      }
      if (depositCalculationMode === 'custom' && !depositCustomTotalAmount) {
        toastify("Custom total amount is required.", "error");
        return false;
      }
    }

    // Payment validation — required whenever there's something to be paid for
    if (depositEnabled || upfrontPaymentEnabled) {
      // Payment validation — OPTIONAL. Deposits/upfront charges can be created
      // unpaid (to be collected later). If the user entered an amount, require
      // a date to go with it.
      if (paymentAmountPaid && parseFloat(paymentAmountPaid) > 0) {
        if (isNaN(paymentAmountPaid)) {
          toastify("Amount paid must be a valid number.", "error");
          return false;
        }
        if (!paymentDate) {
          toastify("Payment date is required when an amount paid is entered.", "error");
          return false;
        }
      }
    }

    return true;
  };

  // "Create Contract" button now opens the review modal instead of submitting directly
  const handleOpenReview = () => {
    if (!validateForm()) return;
    setShowReviewModal(true);
  };

  const handleContractSubmit = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!validateForm()) {
        setLoading(false);
        return;
      }

      // Get the selected levy's payment frequency
      const paymentFrequency = selectedLevy?.collectionFrequency || "Monthly";

      const contractData = {
        contractName,
        levyId: levy,
        unitIds: selectedUnits.length === 1 ? selectedUnits[0] : selectedUnits,
        amount: parseFloat(amount),
        startDate: isActive ? startDate : null, // null keeps contract Inactive
        endDate: null,
        facilityId,
        balanceBroughtForward: parseFloat(balanceBroughtForward) || 0,
        enabledTaxes,
        paymentFrequency,
      };

      // Add upfront payment config if enabled (charge config only)
      if (upfrontPaymentEnabled) {
        contractData.upfrontPayment = {
          enabled: true,
          amount: parseFloat(upfrontAmount),
          amountPerMonth: parseFloat(upfrontAmountPerMonth) || null,
          numberOfMonths: parseInt(numberOfMonths) || null,
          calculationMode: upfrontCalculationMode,
          settlement: upfrontSettlement,
          taxAmount: upfrontTaxTotal || 0,
          totalWithTax: parseFloat(upfrontTotalWithTax) || 0,
        };
      }

      // Add deposit config if enabled (charge config only — no date/reference)
      if (depositEnabled) {
        contractData.deposit = {
          enabled: true,
          amount: parseFloat(depositAmount),
          amountPerMonth: parseFloat(depositAmountPerMonth) || null,
          numberOfMonths: parseInt(depositNumberOfMonths) || null,
          calculationMode: depositCalculationMode
        };
      }

      // Add the actual payment transaction if there's anything to pay for
      if (depositEnabled || upfrontPaymentEnabled) {
        contractData.payment = {
          amountPaid: paymentAmountPaid ? parseFloat(paymentAmountPaid) : 0,
          datePaid: paymentDate || null,
          paymentReference: paymentReference || undefined,
          paymentMethod: paymentMethod || undefined
        };
      }

      // Send the data to the backend
      const response = await makeRequest2(
        `${addContract}/${facilityId}`,
        "POST",
        contractData
      );

      if (response.success) {
        if (response.partial) {
          toastify(`${response.message}. Check errors for failed contracts.`, "warning");
          response.errors?.forEach(error => {
            console.error(`Unit ${error.unitName}:`, error.error);
          });
        } else {
          toastify(response.message || "Levy Agreement(s) added successfully", "success");

          // Show receipt info if created
          if (response.receiptInfo) {
            toastify(response.receiptInfo.message, "info");
          }
        }

        // Keep the official receipt(s) on screen so they can be downloaded.
        // Names are resolved now, before handleClear() wipes the form state.
        const receipts = (response.receiptInfo?.receipts || []).map((r) => ({
          ...r,
          unitName: (units.find((u) => u._id === String(r.unitId)) || {}).name || "",
          customerName: unitCustomerMap[String(r.unitId)]?.name || "",
          issuedOn: new Date().toISOString(),
        }));
        setShowReviewModal(false);
        handleClear();
        if (receipts.length > 0) {
          setIssuedReceipts(receipts);
          setShowIssuedReceipts(true);
        } else {
          navigate("/facility/levy_management/contracts");
        }
      } else {
        console.error("Error response:", response.error);
        toastify(response.error || "Failed to add contract(s).", "error");
      }
    } catch (err) {
      console.error("Error adding contract(s):", err);
      toastify(
        err.message ||
        "An unexpected error occurred while adding the contract(s).",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Clear form
  const handleClear = () => {
    setContractName("");
    setLevy("");
    setSelectedUnits([]);
    setAmount("");
    setIsActive(false);
    setStartDate("");
    setBalanceBroughtForward("0.00");
    setEnabledTaxes([]);
    setIsMultipleMode(false);
    setActiveTabIndex(0);
    setShowLevyDetails(false);

    // Clear upfront payment fields
    setUpfrontPaymentEnabled(false);
    setUpfrontAmount("");
    setUpfrontAmountPerMonth("");
    setNumberOfMonths("");
    setCustomTotalAmount("");
    setUpfrontCalculationMode('auto');
    setUpfrontSettlement('invoices');

    // Clear deposit fields
    setDepositEnabled(false);
    setDepositAmount("");
    setDepositNumberOfMonths("");
    setDepositCustomTotalAmount("");
    setDepositCalculationMode('auto');
    // Don't reset depositAmountPerMonth as it's loaded from levy

    // Clear payment fields
    setPaymentAmountPaid("");
    setPaymentDate("");
    setPaymentReference("");
    setPaymentMethod("mpesa");
  };

  // Load data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchLevies();
        await fetchCustomersForFacility();
        await fetchUnitsForFacility();
        await fetchExistingContracts();
        await fetchTaxRates();
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, []);

  // Update available units when levy or contracts change
  useEffect(() => {
    updateAvailableUnits(units, existingContracts, levy);
  }, [levy, existingContracts, units, customers]);

  // Reset selected units when switching modes or changing levy
  useEffect(() => {
    setSelectedUnits([]);
  }, [isMultipleMode, levy]);

  // Reset upfront, deposit, and payment fields when levy changes
  useEffect(() => {
    // Reset upfront payment
    setUpfrontPaymentEnabled(false);
    setUpfrontAmount("");
    setUpfrontAmountPerMonth("");
    setNumberOfMonths("");
    setCustomTotalAmount("");
    setUpfrontCalculationMode('auto');
    setUpfrontSettlement('invoices');

    // Reset deposit
    setDepositEnabled(false);
    setDepositAmount("");
    setDepositNumberOfMonths("");
    setDepositCustomTotalAmount("");
    setDepositCalculationMode('auto');
    // Don't reset depositAmountPerMonth as it's loaded from levy

    // Reset payment
    setPaymentAmountPaid("");
    setPaymentDate("");
    setPaymentReference("");
    setPaymentMethod("mpesa");
  }, [levy]);

  // Update amounts when levy is selected
  useEffect(() => {
    if (selectedLevy) {
      // Set amount from the selected levy
      setAmount(selectedLevy.amount || "");
      // Set upfront amount per month to match the levy amount
      setUpfrontAmountPerMonth(selectedLevy.amount || "");
      // Set deposit amount per month to match the levy amount
      setDepositAmountPerMonth(selectedLevy.amount || "");
    }
  }, [selectedLevy]);

  // Get frequency text for display
  const getFrequencyText = () => {
    if (!levy) return "";
    return selectedLevy ? selectedLevy.collectionFrequency : "";
  };

  const getSelectedUnitNames = () => {
    return selectedUnits.map(unitId => {
      const unit = availableUnits.find(u => u._id === unitId) || units.find(u => u._id === unitId);
      return unit?.name || unitId;
    });
  };

  const getSelectedCustomerNames = () => {
    return selectedUnits
      .map(unitId => unitCustomerMap[unitId]?.name)
      .filter(Boolean);
  };

  const closeIssuedReceipts = () => {
    setShowIssuedReceipts(false);
    setIssuedReceipts([]);
    navigate("/facility/levy_management/contracts");
  };

  const formatMoney = (n) =>
    Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Renders the on-screen receipt to a PDF (same approach as the deposit receipt page)
  const downloadIssuedReceiptPdf = async (receipt) => {
    const element = document.getElementById(`issued-receipt-${receipt.receiptNumber}`);
    if (!element) return;
    try {
      setDownloadingReceipt(receipt.receiptNumber);
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5" });
      const pdfWidth = 148;
      const pdfHeight = 210;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      if (imgHeight <= pdfHeight) {
        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
      } else {
        const scale = pdfHeight / imgHeight;
        pdf.addImage(imgData, "JPEG", (pdfWidth - pdfWidth * scale) / 2, 0, pdfWidth * scale, pdfHeight);
      }
      pdf.save(`Receipt_${receipt.receiptNumber}.pdf`);
    } catch (err) {
      console.error("Receipt PDF error:", err);
      toastify("Failed to generate the receipt PDF. Please try again.", "error");
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const downloadAllIssuedReceipts = async () => {
    for (const r of issuedReceipts) {
      // eslint-disable-next-line no-await-in-loop
      await downloadIssuedReceiptPdf(r);
    }
  };

  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/levy_management/levies/levy_list">
                    Levy Management
                  </Link>
                </li>
                <li className="breadcrumb-item">Add New Levy Details</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5>Add New Levy Details</h5>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="multipleMode"
                  checked={isMultipleMode}
                  onChange={(e) => setIsMultipleMode(e.target.checked)}
                  disabled={!levy}
                />
                <label className="form-check-label" htmlFor="multipleMode">
                  Select Multiple Units
                </label>
              </div>
            </div>

            <div className="card-body">
              <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>

                {/* ============================================ */}
                {/* TAB 1: LEVY TERMS (name, levy, units, amount, status, taxes) */}
                {/* ============================================ */}
                <TabPanel header="Levy Terms">
                  <div className="container">
                    <div className="row">
                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Levy Name</label>
                        <InputText
                          className="form-control"
                          value={contractName}
                          onChange={(e) => setContractName(e.target.value)}
                          placeholder={isMultipleMode && selectedUnits.length > 1 ? "Base name (unit names will be appended)" : "Enter levy name"}
                        />
                      </div>

                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Levy</label>
                        <select
                          className="form-control"
                          value={levy}
                          onChange={(e) => {
                            const selectedLevyId = e.target.value;
                            setLevy(selectedLevyId);

                            // Find the selected levy
                            const levyObj = levies.find((l) => l._id === selectedLevyId);
                            setSelectedLevy(levyObj);

                            // Set amount from the selected levy
                            setAmount(levyObj ? levyObj.amount : "");
                            // Set upfront amount per month to match the levy amount
                            setUpfrontAmountPerMonth(levyObj ? levyObj.amount : "");
                            // Set deposit amount per month to match the levy amount
                            setDepositAmountPerMonth(levyObj ? levyObj.amount : "");

                            // Clear unit selection
                            setSelectedUnits([]);
                          }}
                        >
                          <option value="">-- Select --</option>
                          {levies.map((levy) => (
                            <option key={levy._id} value={levy._id}>
                              {levy.levyName} ({levy.levyApplicant === "tenant" ? "Tenant" : "Home Owner"})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-12 mb-4">
                        <label className="form-label mb-2">
                          {isMultipleMode ? "Select Units" : "Select Unit"}
                          {isMultipleMode && availableUnits.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm ms-2 p-0"
                              onClick={handleSelectAllUnits}
                            >
                              {selectedUnits.length === availableUnits.length ? "Deselect All" : "Select All"}
                            </button>
                          )}
                        </label>

                        {!levy && (
                          <div className="alert alert-info">
                            Please select a levy first to see available units
                          </div>
                        )}

                        {levy && availableUnits.length === 0 && (
                          <div className="alert alert-warning">
                            No available units found for this levy. All units already have active contracts.
                          </div>
                        )}

                        {levy && availableUnits.length > 0 && (
                          <div className="units-selection-container">
                            {!isMultipleMode ? (
                              // Single selection mode - dropdown
                              <select
                                className="form-control"
                                value={selectedUnits[0] || ""}
                                onChange={(e) => handleUnitSelect(e.target.value)}
                              >
                                <option value="">-- Select --</option>
                                {availableUnits.map((unit) => {
                                  const customerInfo = unitCustomerMap[unit._id];
                                  return (
                                    <option key={unit._id} value={unit._id}>
                                      {unit.name}
                                      {customerInfo && ` - ${customerInfo.name} (${customerInfo.type})`}
                                    </option>
                                  );
                                })}
                              </select>
                            ) : (
                              // Multiple selection mode - checkboxes
                              <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {availableUnits.map((unit) => {
                                  const customerInfo = unitCustomerMap[unit._id];
                                  const isSelected = selectedUnits.includes(unit._id);

                                  return (
                                    <div
                                      key={unit._id}
                                      className={`unit-selection-item p-2 mb-2 rounded ${isSelected ? 'bg-primary bg-opacity-10' : 'bg-light'}`}
                                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                      onClick={() => handleUnitSelect(unit._id)}
                                    >
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => { }}
                                          id={`unit-${unit._id}`}
                                        />
                                        <label
                                          className="form-check-label w-100"
                                          htmlFor={`unit-${unit._id}`}
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                              <strong>{unit.name}</strong>
                                              {customerInfo && (
                                                <div className="small text-muted">
                                                  {customerInfo.name}
                                                  <span className={`badge ms-1 ${customerInfo.isCorrectType ? 'bg-success' : 'bg-warning'}`}>
                                                    {customerInfo.type}
                                                  </span>
                                                  {!customerInfo.isCorrectType && (
                                                    <span className="text-warning ms-1">
                                                      (Fallback)
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              {!customerInfo && (
                                                <div className="small text-danger">
                                                  No customer assigned
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </label>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {selectedUnits.length > 0 && (
                              <div className="mt-3 p-3 bg-light rounded">
                                <h6 className="mb-2">Selected Units ({selectedUnits.length})</h6>
                                <div className="selected-units-summary">
                                  {selectedUnits.map(unitId => {
                                    const unit = availableUnits.find(u => u._id === unitId);
                                    const customerInfo = unitCustomerMap[unitId];
                                    return (
                                      <div key={unitId} className="d-flex justify-content-between align-items-center mb-1">
                                        <span>{unit?.name}</span>
                                        <span className="small text-muted">
                                          {customerInfo ? customerInfo.name : 'No customer'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Amount</label>
                        <input
                          type="text"
                          className="form-control"
                          value={amount}
                          disabled
                        />
                        <small className="text-muted">This value is set in the levy configuration</small>
                      </div>

                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Payment Frequency (from levy)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={getFrequencyText()}
                          disabled
                        />
                        <small className="text-muted">This value is set in the levy configuration</small>
                      </div>

                      {/* STATUS TOGGLE - replaces start/end date inputs */}
                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Contract Status</label>
                        <div className="d-flex align-items-center">
                          <i
                            className={`ti ${isActive
                              ? "ti-toggle-right text-primary"
                              : "ti-toggle-left text-secondary"
                              }`}
                            style={{ fontSize: "34px", cursor: "pointer" }}
                            onClick={() => setIsActive(!isActive)}
                          />
                          <span className="ms-2 fw-bold">{isActive ? "Active" : "Inactive"}</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          By default the contract is <strong>Inactive</strong>. Enable to set it <strong>Active</strong> and provide a start date.
                        </small>
                      </div>

                      {isActive && (
                        <div className="col-sm-6 mb-4">
                          <label className="form-label mb-2">
                            Start Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                          <small className="text-muted">Required to activate the contract</small>
                        </div>
                      )}

                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Balance Brought Forward</label>
                        <input
                          type="text"
                          className="form-control"
                          value={balanceBroughtForward}
                          onChange={(e) => setBalanceBroughtForward(e.target.value)}
                          placeholder="0.00"
                        />
                        <small className="text-muted">
                          {selectedUnits.length > 1
                            ? "This balance will be applied to all selected units"
                            : "Enter any existing balance to be carried over"}
                        </small>
                      </div>

                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Applicable Taxes</label>

                        {fetchedTaxRates.length > 0 ? (
                          fetchedTaxRates.map((tax) => (
                            <div className="form-check" key={tax._id}>
                              <Checkbox
                                inputId={`levy-tax-${tax._id}`}
                                checked={enabledTaxes.includes(tax._id)}
                                onChange={(e) => {
                                  let updated;

                                  if (e.checked) {
                                    updated = [...enabledTaxes, tax._id];
                                  } else {
                                    updated = enabledTaxes.filter(id => id !== tax._id);
                                  }

                                  setEnabledTaxes(updated);
                                }}
                                className="form-check-input"
                              />
                              <label htmlFor={`levy-tax-${tax._id}`} className="form-check-label ms-2">
                                {tax.taxType.toUpperCase()} ({tax.taxRate}%)
                              </label>
                            </div>
                          ))
                        ) : (
                          <small className="text-muted">
                            No taxes configured for this facility
                          </small>
                        )}

                        <small className="text-muted d-block mt-2">
                          Select one or more taxes to apply to the pre-payment
                        </small>
                      </div>

                    </div>
                  </div>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 2: UPFRONT PAYMENT - simplified, months + total only */}
                {/* ============================================ */}
                <TabPanel header="Pre Payment">
                  <div className="container">
                    <div className="row">
                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Pre Payment</label>
                        <div className="d-flex align-items-center">
                          <i
                            className={`ti ${upfrontPaymentEnabled
                              ? "ti-toggle-right text-primary"
                              : "ti-toggle-left text-danger"
                              }`}
                            style={{ fontSize: "34px", cursor: "pointer" }}
                            onClick={() => {
                              if (!levy) {
                                toastify("Please select a levy first", "warning");
                                return;
                              }
                              setUpfrontPaymentEnabled(!upfrontPaymentEnabled);
                            }}
                          />
                          <span className="ms-2 fw-bold">{upfrontPaymentEnabled ? "Enabled" : "Disabled"}</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          {!levy
                            ? "Select a levy first to enable pre-payment"
                            : "Collect advance payments for future billing periods"
                          }
                        </small>
                      </div>

                      {!levy && (
                        <div className="col-12 mb-4">
                          <div className="alert alert-warning">
                            <i className="ti ti-alert-triangle me-2"></i>
                            Please select a levy on the Levy Terms tab first.
                          </div>
                        </div>
                      )}

                      {upfrontPaymentEnabled && levy && (
                        <>
                          <div className="col-12 mb-3">
                            <div className="alert alert-info" style={{ borderLeft: "4px solid #0d6efd" }}>
                              <h6 className="alert-heading mb-2">
                                <i className="ti ti-cash me-2"></i>
                                Pre Payment Configuration
                              </h6>
                              <p className="mb-0">
                                Configure advance payment for future months. Amount per unit is read-only from the levy.
                              </p>
                            </div>
                          </div>

                          <div className="col-12 mb-4">
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="upfrontSettlementCredit"
                                checked={upfrontSettlement === 'credit'}
                                onChange={(e) => setUpfrontSettlement(e.target.checked ? 'credit' : 'invoices')}
                              />
                              <label className="form-check-label fw-bold" htmlFor="upfrontSettlementCredit">
                                Hold as overpayment (credit)
                              </label>
                            </div>
                            <small className="text-muted d-block mt-1">
                              {upfrontSettlement === 'credit'
                                ? "No invoice is created now. The payment is kept as credit and applied automatically to the contract's scheduled invoices as they are generated."
                                : "Creates already-paid invoices for the pre-paid periods immediately."}
                            </small>
                          </div>

                          {/* Amount per month - Read-only */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">
                              Amount per Quantity <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              value={upfrontAmountPerMonth}
                              disabled
                              style={{ backgroundColor: '#e9ecef' }}
                            />
                            <small className="text-muted">Read-only - From levy configuration</small>
                          </div>

                          {/* Calculation Mode */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Calculation Mode</label>
                            <div className="d-flex gap-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="upfrontCalculationMode"
                                  id="upfrontAutoMode"
                                  value="auto"
                                  checked={upfrontCalculationMode === 'auto'}
                                  onChange={() => setUpfrontCalculationMode('auto')}
                                />
                                <label className="form-check-label" htmlFor="upfrontAutoMode">
                                  Auto Calculate
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="upfrontCalculationMode"
                                  id="upfrontCustomMode"
                                  value="custom"
                                  checked={upfrontCalculationMode === 'custom'}
                                  onChange={() => setUpfrontCalculationMode('custom')}
                                />
                                <label className="form-check-label" htmlFor="upfrontCustomMode">
                                  Custom Total
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Auto Calculation - Number of Months */}
                          {upfrontCalculationMode === 'auto' && (
                            <div className="col-sm-6 mb-4">
                              <label className="form-label mb-2">
                                Number of Quantity <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={numberOfMonths}
                                onChange={(e) => setNumberOfMonths(e.target.value)}
                                placeholder="Enter number of units"
                                min="1"
                                step="1"
                              />
                              <small className="text-muted">
                                Total = Amount per unit × Number of quantity
                              </small>
                            </div>
                          )}

                          {/* Custom Total */}
                          {upfrontCalculationMode === 'custom' && (
                            <div className="col-sm-6 mb-4">
                              <label className="form-label mb-2">
                                Custom Total Amount <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={customTotalAmount}
                                onChange={(e) => setCustomTotalAmount(e.target.value)}
                                placeholder="Enter custom total amount"
                                min="0"
                                step="0.01"
                              />
                              <small className="text-muted">Enter the total pre payment directly</small>
                            </div>
                          )}

                          {/* Total Upfront Amount Display */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Total Pre Payment Amount (Before Tax)</label>
                            <input
                              type="text"
                              className="form-control form-control-lg fw-bold"
                              value={upfrontAmount || "0.00"}
                              disabled
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                            <small className="text-muted text-success">
                              {upfrontCalculationMode === 'auto' && numberOfMonths && upfrontAmountPerMonth
                                ? `${upfrontAmountPerMonth} × ${numberOfMonths} = ${upfrontAmount}`
                                : upfrontCalculationMode === 'custom' && customTotalAmount
                                  ? `Custom total: ${customTotalAmount}`
                                  : 'Enter values to calculate'
                              }
                            </small>
                          </div>

                          {enabledTaxes.length > 0 && upfrontAmount && (
                            <div className="col-sm-6 mb-4">
                              <label className="form-label mb-2">Total Pre Payment Amount (Incl. Tax)</label>
                              <input
                                type="text"
                                className="form-control form-control-lg fw-bold text-primary"
                                value={upfrontTotalWithTax}
                                disabled
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                              <small className="text-muted">
                                Taxes are applied only to the pre-payment, never to the deposit
                              </small>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 3: SECURITY DEPOSIT — CONFIG ONLY */}
                {/* No date/reference here anymore — that's captured once, */}
                {/* for the whole contract, on the Payment tab. */}
                {/* ============================================ */}
                <TabPanel header="Security Deposit">
                  <div className="container">
                    <div className="row">
                      <div className="col-sm-6 mb-4">
                        <label className="form-label mb-2">Security Deposit</label>
                        <div className="d-flex align-items-center">
                          <i
                            className={`ti ${depositEnabled
                              ? "ti-toggle-right text-primary"
                              : "ti-toggle-left text-danger"
                              }`}
                            style={{ fontSize: "34px", cursor: "pointer" }}
                            onClick={() => {
                              if (!levy) {
                                toastify("Please select a levy first", "warning");
                                return;
                              }
                              setDepositEnabled(!depositEnabled);
                              // When enabling, set default calculation mode
                              if (!depositEnabled) {
                                setDepositCalculationMode('auto');
                              }
                            }}
                          />
                          <span className="ms-2 fw-bold">{depositEnabled ? "Enabled" : "Disabled"}</span>
                        </div>
                        <small className="text-muted d-block mt-1">
                          {!levy
                            ? "Select a levy first to enable deposit"
                            : "Define the security deposit amount for this contract"
                          }
                        </small>
                      </div>

                      {!levy && (
                        <div className="col-12 mb-4">
                          <div className="alert alert-warning">
                            <i className="ti ti-alert-triangle me-2"></i>
                            Please select a levy on the Levy Terms tab first.
                          </div>
                        </div>
                      )}

                      {depositEnabled && levy && (
                        <>
                          <div className="col-12 mb-3">
                            <div className="alert alert-success" style={{ borderLeft: "4px solid #28a745" }}>
                              <h6 className="alert-heading mb-2">
                                <i className="ti ti-lock me-2"></i>
                                Security Deposit Configuration
                              </h6>
                              <p className="mb-0">
                                This only defines how much the deposit is. Actual payment
                                details (amount paid, date, reference) are captured on the
                                Payment tab. Amount per month is read-only from the levy.
                                No tax is applied to deposits.
                              </p>
                            </div>
                          </div>

                          {/* Amount per month - Read-only from levy */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">
                              Amount per Quantity <span className="text-danger">*</span>
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              value={depositAmountPerMonth}
                              disabled
                              style={{ backgroundColor: '#e9ecef' }}
                            />
                            <small className="text-muted">Read-only - From levy configuration</small>
                          </div>

                          {/* Calculation Mode */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Calculation Mode</label>
                            <div className="d-flex gap-3">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="depositCalculationMode"
                                  id="depositAutoMode"
                                  value="auto"
                                  checked={depositCalculationMode === 'auto'}
                                  onChange={() => setDepositCalculationMode('auto')}
                                />
                                <label className="form-check-label" htmlFor="depositAutoMode">
                                  Auto Calculate
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="depositCalculationMode"
                                  id="depositCustomMode"
                                  value="custom"
                                  checked={depositCalculationMode === 'custom'}
                                  onChange={() => setDepositCalculationMode('custom')}
                                />
                                <label className="form-check-label" htmlFor="depositCustomMode">
                                  Custom Total
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Auto Calculation */}
                          {depositCalculationMode === 'auto' && (
                            <div className="col-sm-6 mb-4">
                              <label className="form-label mb-2">
                                Number of Quantity <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={depositNumberOfMonths}
                                onChange={(e) => setDepositNumberOfMonths(e.target.value)}
                                placeholder="Enter number of quantity"
                                min="1"
                                step="1"
                              />
                              <small className="text-muted">
                                Total = Amount per quantity × Number of quantity
                              </small>
                            </div>
                          )}

                          {/* Custom Total */}
                          {depositCalculationMode === 'custom' && (
                            <div className="col-sm-6 mb-4">
                              <label className="form-label mb-2">
                                Custom Total Amount <span className="text-danger">*</span>
                              </label>
                              <input
                                type="number"
                                className="form-control"
                                value={depositCustomTotalAmount}
                                onChange={(e) => setDepositCustomTotalAmount(e.target.value)}
                                placeholder="Enter custom total amount"
                                min="0"
                                step="0.01"
                              />
                              <small className="text-muted">Enter the total deposit amount directly</small>
                            </div>
                          )}

                          {/* Total Deposit Amount Display */}
                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Total Deposit Amount</label>
                            <input
                              type="text"
                              className="form-control form-control-lg fw-bold"
                              value={depositAmount || "0.00"}
                              disabled
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                            <small className="text-muted text-success">
                              {depositCalculationMode === 'auto' && depositNumberOfMonths && depositAmountPerMonth
                                ? `${depositAmountPerMonth} × ${depositNumberOfMonths} = ${depositAmount}`
                                : depositCalculationMode === 'custom' && depositCustomTotalAmount
                                  ? `Custom total: ${depositCustomTotalAmount}`
                                  : 'Enter values to calculate'
                              }
                            </small>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </TabPanel>

                {/* ============================================ */}
                {/* TAB 4: PAYMENT — NEW. The single source of truth for what */}
                {/* was actually paid against this contract's charges. */}
                {/* ============================================ */}
                <TabPanel header="Payment">
                  <div className="container">
                    <div className="row">
                      {!depositEnabled && !upfrontPaymentEnabled ? (
                        <div className="col-12 mb-4">
                          <div className="alert alert-info">
                            <i className="ti ti-info-circle me-2"></i>
                            Enable a Security Deposit or Pre Payment to capture a payment here.
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="col-12 mb-3">
                            <div className="alert alert-primary" style={{ borderLeft: "4px solid #0d6efd" }}>
                              <h6 className="alert-heading mb-2">
                                <i className="ti ti-receipt me-2"></i>
                                Payment Received
                              </h6>
                              <p className="mb-0">
                                Enter the actual amount received for this contract. This is
                                independent of how the deposit/pre-payment charges are configured —
                                so over or under payments are reflected accurately.
                              </p>
                            </div>
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Total Charges (for reference)</label>
                            <input
                              type="text"
                              className="form-control form-control-lg"
                              value={totalCharges}
                              disabled
                              style={{ backgroundColor: '#e9ecef' }}
                            />
                            <small className="text-muted">
                              {depositEnabled && `Deposit: ${depositAmount || "0.00"}`}
                              {depositEnabled && upfrontPaymentEnabled && " + "}
                              {upfrontPaymentEnabled && `Upfront (incl. tax): ${upfrontTotalWithTax}`}
                            </small>
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">
                              Amount Paid
                            </label>
                            <input
                              type="number"
                              className="form-control form-control-lg"
                              value={paymentAmountPaid}
                              onChange={(e) => setPaymentAmountPaid(e.target.value)}
                              placeholder="Leave blank if not yet paid"
                              min="0"
                              step="0.01"
                            />
                            <small className="text-muted">The real transaction amount — leave empty to create this as unpaid</small>
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">
                              Date Paid {paymentAmountPaid && parseFloat(paymentAmountPaid) > 0 && <span className="text-danger">*</span>}
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={paymentDate}
                              onChange={(e) => setPaymentDate(e.target.value)}
                            />
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Payment Method</label>
                            <select
                              className="form-control"
                              value={paymentMethod}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                              <option value="mpesa">M-Pesa</option>
                              <option value="bank">Bank Transfer</option>
                              <option value="cash">Cash</option>
                              <option value="cheque">Cheque</option>
                            </select>
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">Payment Reference</label>
                            <input
                              type="text"
                              className="form-control"
                              value={paymentReference}
                              onChange={(e) => setPaymentReference(e.target.value)}
                              placeholder="Optional - Enter payment reference"
                            />
                          </div>

                          <div className="col-sm-6 mb-4">
                            <label className="form-label mb-2">
                              {isOverpaid ? "Overpayment" : "Balance Due"}
                            </label>
                            <input
                              type="text"
                              className={`form-control form-control-lg fw-bold ${isOverpaid ? "text-success" : "text-danger"}`}
                              value={isOverpaid ? overpaymentAmount : balanceDue}
                              disabled
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                            <small className="text-muted">
                              {isOverpaid
                                ? "Amount paid exceeds total charges"
                                : "Total charges minus amount paid"}
                            </small>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </TabPanel>
              </TabView>

              <div className="text-end mt-4">
                <Button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </Button>
                &nbsp;
                <Button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleOpenReview}
                  disabled={loading || selectedUnits.length === 0}
                >
                  {selectedUnits.length > 1
                    ? `Review ${selectedUnits.length} Contracts`
                    : "Review Contract"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* REVIEW MODAL - receipt-style preview shown before final submit */}
      {/* ============================================ */}
      <Dialog
        header={null}
        visible={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        style={{ width: '460px' }}
        modal
        showHeader={false}
        className="receipt-dialog"
      >
        <div className="receipt-paper">
          {/* Receipt Header */}
          <div className="receipt-header text-center">
            <i className="ti ti-receipt receipt-icon"></i>
            <h4 className="mb-0 mt-2">Receipt</h4>
            <small className="text-muted">Payment Preview</small>
            <div className="receipt-meta mt-2">
              <span>{today}</span>
            </div>
          </div>

          <div className="receipt-divider"></div>

          {/* Who / what this receipt is for */}
          <div className="receipt-section">
            <div className="d-flex justify-content-between mt-1">
              <span className="text-muted">{selectedUnits.length > 1 ? "Units" : "Unit"}</span>
              <strong className="text-end">{getSelectedUnitNames().join(", ") || "None selected"}</strong>
            </div>
            {getSelectedCustomerNames().length > 0 && (
              <div className="d-flex justify-content-between mt-1">
                <span className="text-muted">Customer{getSelectedCustomerNames().length > 1 ? "s" : ""}</span>
                <strong className="text-end">{getSelectedCustomerNames().join(", ")}</strong>
              </div>
            )}
          </div>

          {!upfrontPaymentEnabled && !depositEnabled && (
            <div className="receipt-section">
              <div className="alert alert-light text-center text-muted mb-0">
                No pre-payment or deposit configured for this contract.
              </div>
            </div>
          )}

          {/* Upfront Payment line items */}
          {upfrontPaymentEnabled && (
            <>
              <div className="receipt-divider-dashed"></div>
              <div className="receipt-section">
                <div className="receipt-line-title">Pre Payment</div>
                <div className="d-flex justify-content-between receipt-line">
                  <span>
                    Subtotal
                    {upfrontCalculationMode === 'auto' && numberOfMonths
                      ? ` (${upfrontAmountPerMonth} × ${numberOfMonths} mo)`
                      : ''}
                  </span>
                  <span>{upfrontAmount || "0.00"}</span>
                </div>

                {upfrontTaxBreakdown.length > 0 ? (
                  upfrontTaxBreakdown.map((t, i) => (
                    <div key={i} className="d-flex justify-content-between receipt-line text-muted">
                      <span className="ps-3">{t.label}</span>
                      <span>+ {t.amount}</span>
                    </div>
                  ))
                ) : (
                  <div className="d-flex justify-content-between receipt-line text-muted">
                    <span className="ps-3">Tax</span>
                    <span>None applied</span>
                  </div>
                )}

                <div className="d-flex justify-content-between receipt-subtotal">
                  <span>Pre-Payment Total</span>
                  <span>{upfrontTotalWithTax}</span>
                </div>
              </div>
            </>
          )}

          {/* Deposit line items - never taxed */}
          {depositEnabled && (
            <>
              <div className="receipt-divider-dashed"></div>
              <div className="receipt-section">
                <div className="receipt-line-title">Security Deposit</div>
                <div className="d-flex justify-content-between receipt-line">
                  <span>
                    Amount
                    {depositCalculationMode === 'auto' && depositNumberOfMonths
                      ? ` (${depositAmountPerMonth} × ${depositNumberOfMonths} mo)`
                      : ''}
                  </span>
                  <span>{depositAmount || "0.00"}</span>
                </div>
                <div className="d-flex justify-content-between receipt-subtotal">
                  <span>Deposit Total</span>
                  <span>{depositAmount || "0.00"}</span>
                </div>
              </div>
            </>
          )}

          {/* Payment section - the actual transaction */}
          {(upfrontPaymentEnabled || depositEnabled) && (
            <>
              <div className="receipt-divider"></div>
              <div className="receipt-section">
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Total Charges</span>
                  <strong>{totalCharges}</strong>
                </div>
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Amount Paid</span>
                  <strong>{paymentAmountPaid || "0.00"}</strong>
                </div>
                <div className="d-flex justify-content-between receipt-line text-muted mt-1">
                  <span>Date Paid</span>
                  <span>{paymentDate || "Not set"}</span>
                </div>
                {paymentReference && (
                  <div className="d-flex justify-content-between receipt-line text-muted">
                    <span>Reference</span>
                    <span>{paymentReference}</span>
                  </div>
                )}
              </div>
              <div className="receipt-divider"></div>
              <div className="receipt-section">
                <div className="d-flex justify-content-between receipt-grand-total">
                  <span>{isOverpaid ? "OVERPAYMENT" : "BALANCE DUE"}</span>
                  <span>{isOverpaid ? overpaymentAmount : balanceDue}</span>
                </div>
              </div>
            </>
          )}

          <div className="receipt-divider"></div>

          {/* Levy details - collapsed by default, shown separately on demand */}
          <div className="receipt-section">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => setShowLevyDetails(!showLevyDetails)}
            >
              {showLevyDetails ? "Hide" : "View"} Levy Terms
              <i className={`ti ${showLevyDetails ? "ti-chevron-up" : "ti-chevron-down"} ms-1`}></i>
            </button>

            {showLevyDetails && (
              <table className="table table-sm mt-2 mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted">Levy</td>
                    <td className="text-end"><strong>{selectedLevy?.levyName || "Not selected"}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Amount per Unit</td>
                    <td className="text-end"><strong>{amount || "0"}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Payment Frequency</td>
                    <td className="text-end"><strong>{getFrequencyText()}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Status</td>
                    <td className="text-end"><strong>{isActive ? "Active" : "Inactive"}</strong></td>
                  </tr>
                  {isActive && (
                    <tr>
                      <td className="text-muted">Start Date</td>
                      <td className="text-end"><strong>{startDate}</strong></td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">Balance Brought Forward</td>
                    <td className="text-end"><strong>{balanceBroughtForward || "0.00"}</strong></td>
                  </tr>
                  <tr>
                    <td className="text-muted">Selected Taxes</td>
                    <td className="text-end">
                      <strong>
                        {enabledTaxes.length > 0
                          ? fetchedTaxRates
                            .filter(t => enabledTaxes.includes(t._id))
                            .map(t => `${t.taxType.toUpperCase()} (${t.taxRate}%)`)
                            .join(", ")
                          : "None"}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <div className="receipt-footer text-center text-muted mt-3">
            <small>This is a preview. The official receipt is generated after confirmation.</small>
          </div>
        </div>

        <div className="text-end mt-4">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setShowReviewModal(false)}
            disabled={loading}
          >
            Back to Edit
          </Button>
          &nbsp;
          <Button
            type="button"
            className="btn btn-primary"
            onClick={handleContractSubmit}
            disabled={loading}
          >
            {loading
              ? "Submitting..."
              : selectedUnits.length > 1
                ? `Confirm & Create ${selectedUnits.length} Contracts`
                : "Confirm & Create Contract"}
          </Button>
        </div>
      </Dialog>


      {/* ============================================ */}
      {/* ISSUED RECEIPTS - official receipt(s) shown after creation */}
      {/* ============================================ */}
      <Dialog
        header="Receipt issued"
        visible={showIssuedReceipts}
        onHide={closeIssuedReceipts}
        style={{ width: '500px' }}
        modal
      >
        {issuedReceipts.map((r) => (
          <div key={r.receiptNumber} className="mb-4">
            <div id={`issued-receipt-${r.receiptNumber}`} className="receipt-paper">
              <div className="text-center">
                <i className="ti ti-receipt receipt-icon"></i>
                <h4 className="mb-0 mt-2">Receipt</h4>
                <small className="text-muted">#{r.receiptNumber}</small>
              </div>
              <div className="receipt-divider"></div>
              <div className="receipt-section">
                {r.customerName && (
                  <div className="d-flex justify-content-between receipt-line">
                    <span className="text-muted">Received From</span>
                    <strong className="text-end">{r.customerName}</strong>
                  </div>
                )}
                {r.unitName && (
                  <div className="d-flex justify-content-between receipt-line">
                    <span className="text-muted">Unit</span>
                    <strong>{r.unitName}</strong>
                  </div>
                )}
                <div className="d-flex justify-content-between receipt-line text-muted">
                  <span>Paid Date</span>
                  <span>
                    {r.datePaid
                      ? new Date(r.datePaid).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                      : "Not paid"}
                  </span>
                </div>
                {r.paymentReference && (
                  <div className="d-flex justify-content-between receipt-line text-muted">
                    <span>Reference</span>
                    <span>{r.paymentReference}</span>
                  </div>
                )}
              </div>
              <div className="receipt-divider-dashed"></div>
              <div className="receipt-section">
                {(r.lineItems || []).map((li, i) => (
                  <div key={i} className="d-flex justify-content-between receipt-line">
                    <span>{li.description}</span>
                    <span>{formatMoney(li.unitAmount * (li.quantity || 1))}</span>
                  </div>
                ))}
              </div>
              <div className="receipt-divider"></div>
              <div className="receipt-section">
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Subtotal</span>
                  <span>{formatMoney(r.subTotal)}</span>
                </div>
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Tax</span>
                  <span>{formatMoney(r.taxTotal)}</span>
                </div>
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Total Charges</span>
                  <strong>{formatMoney(r.totalCharges)}</strong>
                </div>
                <div className="d-flex justify-content-between receipt-line">
                  <span className="text-muted">Amount Paid</span>
                  <strong>{formatMoney(r.amountPaid)}</strong>
                </div>
              </div>
              <div className="receipt-divider"></div>
              <div className="receipt-section">
                <div className="d-flex justify-content-between receipt-grand-total">
                  <span>{r.overpaymentAmount > 0 ? "OVERPAYMENT" : "BALANCE DUE"}</span>
                  <span>{formatMoney(r.overpaymentAmount > 0 ? r.overpaymentAmount : r.balanceDue)}</span>
                </div>
              </div>
              <div className="receipt-footer text-center text-muted mt-3">
                <small>This is a computer generated receipt.</small>
              </div>
            </div>
            <div className="text-end mt-2">
              <Button
                type="button"
                icon="ti ti-download"
                label={downloadingReceipt === r.receiptNumber ? "Generating..." : "Download PDF"}
                className="btn btn-outline-primary"
                disabled={downloadingReceipt !== null}
                onClick={() => downloadIssuedReceiptPdf(r)}
              />
            </div>
          </div>
        ))}
        <div className="d-flex justify-content-between mt-3">
          {issuedReceipts.length > 1 ? (
            <Button
              type="button"
              label="Download all"
              icon="ti ti-download"
              className="btn btn-outline-primary"
              disabled={downloadingReceipt !== null}
              onClick={downloadAllIssuedReceipts}
            />
          ) : <span />}
          <Button type="button" label="Done" className="btn btn-primary" onClick={closeIssuedReceipts} />
        </div>
      </Dialog>

      <style jsx>{`
        .unit-selection-item:hover {
          transform: translateX(5px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .selected-units-summary {
          max-height: 150px;
          overflow-y: auto;
        }
        
        .form-check-input {
          cursor: pointer;
        }
        
        .badge {
          font-size: 0.75rem;
          padding: 0.2rem 0.4rem;
        }

        /* ============ RECEIPT STYLING ============ */
        .receipt-paper {
          font-family: 'Courier New', Courier, monospace;
          background: #fff;
          padding: 8px 4px;
        }

        .receipt-icon {
          font-size: 28px;
          color: #495057;
        }

        .receipt-meta {
          font-size: 0.8rem;
          color: #6c757d;
        }

        .receipt-divider {
          border-top: 2px dashed #adb5bd;
          margin: 14px 0;
        }

        .receipt-divider-dashed {
          border-top: 1px dashed #ced4da;
          margin: 12px 0;
        }

        .receipt-section {
          padding: 0 4px;
        }

        .receipt-line-title {
          font-weight: 700;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 0.5px;
          color: #495057;
          margin-bottom: 6px;
        }

        .receipt-line {
          font-size: 0.9rem;
          padding: 2px 0;
        }

        .receipt-subtotal {
          font-size: 0.92rem;
          font-weight: 700;
          padding-top: 6px;
          margin-top: 4px;
          border-top: 1px solid #e9ecef;
        }

        .receipt-grand-total {
          font-size: 1.15rem;
          font-weight: 800;
          letter-spacing: 0.5px;
        }

        .receipt-footer small {
          font-size: 0.72rem;
        }
      `}</style>
    </Layout>
  );
};

export default AddContract;